///
/// EarthBrowser
///
/// Created by Matt Giger on 3/25/2024
/// Copyright (c) 2024 Geodata Labs
///

import { EntityCameraAdapter } from "@/earthbrowser/datasets/EntityCameraAdapter"
import { type InfoPanelData, type InfoPanelProvider } from "@/earthbrowser/datasets/InfoPanelProvider"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { GLTF } from "@/earthbrowser/entities/GLTF/GLTF"
import { PolylineNode } from "@/earthbrowser/entities/Polyline/PolylineNode"
import { wgs } from "@/earthbrowser/utils/geographic"
import { engineActions, engineBridge } from "@/earthbrowser/engineBridge"
import { pushTargetToHistory } from "@/utils/navigationHistory"
import type { Camera } from "@stovokor/camera/Camera"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { type RenderActionSet } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type FocusTransitionConfig } from "@stovokor/gestures/FocusTarget"
import { GestureNode } from "@stovokor/gestures/GestureNode"
import { type GestureHitResult } from "@stovokor/gestures/GestureRecognizer"
import { cross, matrix, normal, quaternion, rgba, sphere, type time, trig, vec3 } from "@stovokor/math"
import { eciToGeodetic, gstime, propagate, type SatRec } from "satellite.js"
import { type DatasetSceneObject } from "../DatasetLayer"
import { classifySatellite } from "./satelliteArchetype"
import { type SatelliteDatasetLayer, type SatelliteFeature } from "./SatelliteDatasetLayer"
import { SatelliteMarkerNode } from "./SatelliteMarkerNode"
import { sampleOrbitPath, satrecFrom } from "./satelliteOrbit"

// Hero models: real glTF geometry for a few famous spacecraft, loaded
// lazily per node and drawn in place of the procedural archetype when the
// camera is close. Everything else is an archetype (SatelliteMarkerNode)
const cModelURLs: Record<string, string> = {
	'ISS (ZARYA)': 'https://tiles.earthbrowser.com/static/models/ISS_stationary.glb',
}

const cHitSphereRadius = 10
const cModelScale = 0.2
// const cMinimumUpdateInterval = 50 // milliseconds

// Camera distance (km) under which a hero 3D model is rendered
const cModelVisibleDistance = 5500

const cSatelliteFocusDistance = 200
const cSatelliteFocusMaxZoomRadius = 500

// While focused, cap the clock speed so one orbit takes at least this many
// wall-clock seconds. Derived from the orbital period, so LEO satellites cap
// around ~120x while GEO satellites (which barely move on screen) allow ~1900x.
const cMinSecondsPerOrbit = 45
const cFallbackSpeedCap = 120

// Focused orbit path visualization
const cOrbitRefreshIntervalMs = 2500       // How often to resample the path while focused
const cOrbitFadeDurationSec = 0.8          // Fade in/out duration, matches the focus transition feel
const cOrbitTrailFraction = 0.12           // Fraction of one period shown as the bright trail behind
const cOrbitAheadSamples = 240
const cOrbitTrailSamples = 48
const cOrbitAheadWidth = 1.5
const cOrbitTrailWidth = 2.5
const cOrbitAheadColor = rgba(0.2, 0.8, 1.0, 0.3)    // Dim path ahead (one full period)
const cOrbitTrailColor = rgba(0.55, 0.95, 1.0, 0.85) // Bright trail behind

export class SatelliteNode extends GestureNode implements DatasetSceneObject<SatelliteFeature>, InfoPanelProvider {
	public readonly id: string
	public readonly focusable: boolean = true
	public feature: SatelliteFeature

	private satelliteRecord: SatRec
	private intersectingSphere = new sphere(cHitSphereRadius)
	private marker: SatelliteMarkerNode
	private modelContainer = new NodeBase()
	private modelLoaded = false
	private modelRadius = cHitSphereRadius
	private cameraAdapter: EntityCameraAdapter
	private isFocused: boolean = false
	private layer: SatelliteDatasetLayer

	// Live orbital data updated each frame via SGP4 propagation
	public liveLat: number = 0
	public liveLon: number = 0
	public liveAlt: number = 0
	public liveSpeed: number = 0

	// Focused orbit path: dim line one full period ahead, bright trail behind.
	// Positions are in world (earth-fixed) coordinates, so these nodes are
	// parented to the layer rather than to this (moving) node.
	private orbitPathNode?: PolylineNode
	private orbitTrailNode?: PolylineNode
	private orbitOpacity = 0
	private orbitFadeTarget = 0
	private lastOrbitSampleMs = 0
	private lastFrameMs = 0

	// Initial rotation to orient the model correctly (can be set per model type)
	// Example: To rotate 90° around Y-axis: quaternion.fromAngleAxis(Math.PI / 2, vec3.unitY)
	// Example: To rotate 180° around X-axis: quaternion.fromAngleAxis(Math.PI, vec3.unitX)
	public initialRotation: quaternion = new quaternion(0, 0, 0, 1) // Identity quaternion (no rotation)

	public get satrec(): SatRec {
		return this.satelliteRecord
	}

	public get focused(): boolean {
		return this.isFocused
	}

	constructor(context: EarthBrowserContext, feature: SatelliteFeature, parent: SatelliteDatasetLayer) {
		super(context)
		this.feature = feature
		this.id = `satellite-${feature.object_id}`
		this.satelliteRecord = satrecFrom(feature)
		this.layer = parent

		// Ember dome (orbit-regime tint, no pulse) plus the procedural
		// archetype for this kind of object, in the nadir / along-track frame
		// that update() builds
		this.marker = new SatelliteMarkerNode(context, classifySatellite(feature.object_name, feature.group_ids, feature.mean_motion))
		this.addNode(this.marker)

		// Hero glTF container, filled by load() for the few named spacecraft
		this.modelContainer.transform = matrix.scale(cModelScale)
		this.modelContainer.visible = false
		this.addNode(this.modelContainer)

		if(cModelURLs[feature.object_name] !== undefined) {
			void this.load()
		}

		this.cameraAdapter = new EntityCameraAdapter(context, this, cSatelliteFocusDistance, cSatelliteFocusMaxZoomRadius)

		// Propagate the position immediately so the transform is valid before the
		// first render. A launch deep-link (?target=sat-...) focuses this node right
		// after construction, and a zero-length position produces NaN angles in
		// EntityCameraState (geographic latitude of the origin is undefined), which
		// surfaces as a "Matrix is singular" throw during the focus transition.
		this.update(context.clock.clockTime)
	}
	// ========== InfoPanelProvider ==========

	public getInfoPanelData(): InfoPanelData {
		return {
			title: this.feature.object_name,
			theme: "satellite",
			contentType: "satellite",
			contentData: {
				type: "satellite",
				satellite: this.feature,
				livePosition: {
					lat: this.liveLat,
					lon: this.liveLon,
					alt: this.liveAlt
				},
				liveSpeed: this.liveSpeed
			}
		}
	}

	// ========== Focus Lifecycle ==========

	public onFocusGained(camera: Camera): void {
		this.cameraAdapter.attach(camera)
		this.isFocused = true

		// Cap the clock speed based on orbital period so the tracking camera
		// stays watchable (mean_motion is in revolutions per day)
		const periodSeconds = this.feature.mean_motion > 0 ? 86400 / this.feature.mean_motion : 0
		const speedCap = periodSeconds > 0
			? Math.max(cFallbackSpeedCap, Math.round(periodSeconds / cMinSecondsPerOrbit))
			: cFallbackSpeedCap
		engineBridge.dispatch({ type: engineActions.setClockSpeedCap, payload: speedCap })

		// Fade in the orbit path (built on the next frame)
		this.orbitFadeTarget = 1
		this.lastOrbitSampleMs = 0

		// Push to browser history for back/forward navigation
		const satelliteId = this.feature.object_id
		if (satelliteId) {
			pushTargetToHistory('sat', satelliteId)
		}
	}

	public onFocusLost(): void {
		this.cameraAdapter.detach()
		this.isFocused = false

		// Remove the clock speed cap (FocusManager calls onFocusLost before the
		// next target's onFocusGained, so a new satellite re-applies its own cap)
		engineBridge.dispatch({ type: engineActions.setClockSpeedCap, payload: null })

		// Fade out the orbit path (removed once fully transparent)
		this.orbitFadeTarget = 0
	}

	public onFocusUpdate(): void {
		this.cameraAdapter.update()
	}

	public focusTransition(camera: Camera): FocusTransitionConfig {
		return {
			target: () => this.cameraAdapter.getTargetCameraState(camera),
			params: {
				duration: 1,
				easing: "easeInOut"
			}
		}
	}

	// ========== Command Handlers ==========

	public handleCommand(command: string, data?: unknown): boolean {
		// Delegate camera and compass commands to the adapter
		return this.cameraAdapter.handleCommand(command, data)
	}

	public async load(): Promise<void> {
		const customModel = new NodeBase()
		this.modelContainer.addNode(customModel)

		const gltf = new GLTF(this.context)
		const modelURL = cModelURLs[this.feature.object_name]
		if(modelURL) {
			const subNodes = await gltf.loadModel(modelURL)
			customModel.addNode(subNodes)
			customModel.transform = matrix.axisRotation(vec3.unitZ, trig.half_pi)
			this.addNode(customModel)

			// Set model-specific initial rotations here if needed
			// Example for ISS:
			// if(this.entry.object_name === 'ISS (ZARYA)') {
			//   this.initialRotation = quaternion.fromAngleAxis(Math.PI / 2, vec3.unitY)
			// }
		}

		const bounds = this.calculateBoundingBox()
		this.modelRadius = Math.max(cHitSphereRadius, bounds.sphere.radius)
		this.modelLoaded = true
	}

	override render(state: RenderState): RenderActionSet[] {
		if(!this.visible) {
			return []
		}

		const ebcontext = this.context as EarthBrowserContext
		this.update(ebcontext.clock.clockTime)
		this.updateOrbitPath(ebcontext.clock.clockTime)

		// Dim (but keep) the dome while focused so it doesn't wash out the
		// archetype or model, matching the volcano/quake focus behavior.
		// Reuses the orbit path fade so the dome dims in sync with the path
		// appearing.
		this.marker.domeFade = 1 - 0.9 * this.orbitOpacity

		// A hero model replaces the archetype while the camera is close
		// enough to see it
		const distance = state.camera.position.subtract(this.transform.position).length
		const modelVisible = this.modelLoaded && distance < cModelVisibleDistance
		this.modelContainer.visible = modelVisible
		this.marker.archetypeVisible = !modelVisible

		// Screen-space assist for the archetype. World size of one logical
		// pixel at this depth comes from the projection's vertical scale
		// (perspective: 1/tan(fov/2), where clip w is the depth; ortho: w = 1)
		const p = state.projection.glArrayAccess()
		const depth = state.viewDepthOf(this.transform.position)
		const clipW = p[11] * -depth + p[15]
		const worldPerPixel = p[5] > 0 && state.height > 0 ? (2 * Math.abs(clipW)) / (p[5] * state.height) : 0
		this.marker.setWorldPerPixel(worldPerPixel)

		// Keep the pick volume on whatever is drawn: the archetype's reach,
		// the hero model, or at least the base hit sphere
		const hitRadius = Math.max(cHitSphereRadius, this.marker.extentKm, modelVisible ? this.modelRadius : 0)
		if(Math.abs(hitRadius - this.intersectingSphere.radius) > hitRadius * 0.05) {
			this.intersectingSphere = new sphere(hitRadius)
		}

		return super.render(state)
	}

	override gestureIntersection(state: RenderState): GestureHitResult | undefined {
		const dist = this.intersectingSphere.intersect(state.ray)
		if(dist > 0) {
			const hitPoint = state.ray.pointAt(dist)
			return { node: this, hitPoint: hitPoint, distance: dist }
		}
		return undefined
	}

	/*
	 * Optimized location update for performance
	 */
	private update(currentTime: time): void {

		const positionAndVelocity = propagate(this.satelliteRecord, currentTime.date)
		if(positionAndVelocity.position instanceof Object) {
			const gmst = gstime(currentTime.date)
			const lla = eciToGeodetic(positionAndVelocity.position, gmst)
			const elev = lla.height + wgs.a
			const coslat = Math.cos(lla.latitude) * elev
			const x = Math.sin(lla.longitude) * coslat
			const y = Math.sin(lla.latitude) * elev
			const z = Math.cos(lla.longitude) * coslat

			const length = Math.sqrt(x * x + y * y + z * z)
			const xAxis = new vec3(-x / length, -y / length, -z / length)

			let yAxis: vec3
			if(positionAndVelocity.velocity instanceof Object) {
				const vel = positionAndVelocity.velocity
				yAxis = normal(new vec3(vel.x, vel.y, vel.z))
				this.liveSpeed = Math.sqrt(vel.x * vel.x + vel.y * vel.y + vel.z * vel.z)
			} else {
				yAxis = vec3.unitY
			}

			const zAxis = normal(cross(xAxis, yAxis))
			yAxis = normal(cross(zAxis, xAxis))

			this.transform = new matrix([
				xAxis.x, xAxis.y, xAxis.z, 0,
				yAxis.x, yAxis.y, yAxis.z, 0,
				zAxis.x, zAxis.y, zAxis.z, 0,
				x, y, z, 1
			])

			this.liveLat = lla.latitude * trig.deg
			this.liveLon = lla.longitude * trig.deg
			this.liveAlt = lla.height

			if(this.isFocused) {
				engineBridge.dispatch({
					type: engineActions.updateSatellitePosition,
					payload: {
						lat: this.liveLat,
						lon: this.liveLon,
						alt: this.liveAlt
					}
				})
			}
		}
	}

	// ========== Orbit Path ==========

	/*
	 * Fade the focused orbit path in/out and periodically resample it so the
	 * bright trail keeps tracking the satellite as it moves
	 */
	private updateOrbitPath(now: time): void {
		const nowMs = performance.now()
		const dt = this.lastFrameMs > 0 ? Math.min((nowMs - this.lastFrameMs) / 1000, 0.1) : 1 / 60
		this.lastFrameMs = nowMs

		if(!this.orbitPathNode && this.orbitFadeTarget <= 0) {
			return
		}

		// Step opacity toward the fade target
		const step = dt / cOrbitFadeDurationSec
		if(this.orbitOpacity < this.orbitFadeTarget) {
			this.orbitOpacity = Math.min(this.orbitFadeTarget, this.orbitOpacity + step)
		} else if(this.orbitOpacity > this.orbitFadeTarget) {
			this.orbitOpacity = Math.max(this.orbitFadeTarget, this.orbitOpacity - step)
		}

		// Fully faded out: tear down
		if(this.orbitOpacity <= 0 && this.orbitFadeTarget <= 0) {
			this.removeOrbitPath()
			return
		}

		// Dim ahead-path only needs occasional resampling; the bright trail is
		// resampled every frame so its head stays attached to the satellite
		if(nowMs - this.lastOrbitSampleMs > cOrbitRefreshIntervalMs) {
			this.refreshAheadPath(now)
			this.lastOrbitSampleMs = nowMs
		}
		this.refreshTrail(now)

		this.orbitPathNode?.updateColor(rgba(cOrbitAheadColor.r, cOrbitAheadColor.g, cOrbitAheadColor.b, cOrbitAheadColor.a * this.orbitOpacity))
		this.orbitTrailNode?.updateColor(rgba(cOrbitTrailColor.r, cOrbitTrailColor.g, cOrbitTrailColor.b, cOrbitTrailColor.a * this.orbitOpacity))
	}

	private orbitPeriodSec(): number {
		return (trig.two_pi / this.satelliteRecord.no) * 60
	}

	/*
	 * Sample one full period ahead (dim) in earth-fixed world coordinates
	 */
	private refreshAheadPath(now: time): void {
		const periodSec = this.orbitPeriodSec()
		if(!isFinite(periodSec) || periodSec <= 0) {
			return
		}

		const aheadPositions = sampleOrbitPath(this.satelliteRecord, now.date, periodSec, cOrbitAheadSamples)
		if(aheadPositions.length < 2) {
			return
		}

		if(this.orbitPathNode) {
			this.orbitPathNode.updatePositions(aheadPositions)
		} else {
			this.orbitPathNode = new PolylineNode(this.context, {
				positions: aheadPositions,
				width: cOrbitAheadWidth,
				color: rgba(cOrbitAheadColor.r, cOrbitAheadColor.g, cOrbitAheadColor.b, 0),
				depthWrite: false
			})
			this.layer.addNode(this.orbitPathNode)
		}
	}

	/*
	 * Sample the bright trail behind the satellite, ending exactly at its
	 * current position. Runs every frame; the constant sample count means the
	 * polyline refills its GPU buffer in place instead of recreating it
	 */
	private refreshTrail(now: time): void {
		const periodSec = this.orbitPeriodSec()
		if(!isFinite(periodSec) || periodSec <= 0) {
			return
		}

		const trailSec = cOrbitTrailFraction * periodSec
		const trailPositions = sampleOrbitPath(this.satelliteRecord, new Date(now.date.getTime() - trailSec * 1000), trailSec, cOrbitTrailSamples)
		if(trailPositions.length < 2) {
			return
		}

		if(this.orbitTrailNode) {
			this.orbitTrailNode.updatePositions(trailPositions)
		} else {
			this.orbitTrailNode = new PolylineNode(this.context, {
				positions: trailPositions,
				width: cOrbitTrailWidth,
				color: rgba(cOrbitTrailColor.r, cOrbitTrailColor.g, cOrbitTrailColor.b, 0),
				depthWrite: false
			})
			this.layer.addNode(this.orbitTrailNode)
		}
	}

	private removeOrbitPath(): void {
		if(this.orbitPathNode) {
			this.layer.removeNode(this.orbitPathNode)
			this.orbitPathNode.destroy()
			this.orbitPathNode = undefined
		}
		if(this.orbitTrailNode) {
			this.layer.removeNode(this.orbitTrailNode)
			this.orbitTrailNode.destroy()
			this.orbitTrailNode = undefined
		}
		this.orbitOpacity = 0
	}

	/*
	 * Clean up orbit path when satellite is removed
	 */
	override destroy(): void {
		this.removeOrbitPath()
		super.destroy()
	}
}
